// Logic for the daily review dashboard - see doc/daily-review-concept.org.
//
// Everything here is pure and the clock is always a parameter (`today`, a Date)
// - exactly like lib/contract.js, lib/focus.js, lib/team.js and lib/formation.js
// - so all of it unit-tests without rendering and without freezing timers.
//
// One state object, `{ values, reviews, plans, thankfulness }`, living in one
// persistent file (lib/review_org.js). The reviews and plans are daily-cadence
// and the thankfulness deliberately is not (see below), so a single file holding
// the whole practice reads better than splitting it across daily notes.
//
// Three things are absent on purpose, each because the evidence says so rather
// than for taste (concept §3):
//
//   - No streak, on anything. A streak forces a daily cadence, and the only
//     direct evidence on cadence (Lyubomirsky et al. 2005) has counting
//     blessings once a week raising well-being where three times a week did not.
//     A streak would mechanise exactly the practice whose value depends on not
//     being mechanical.
//   - No mood or well-being score, and so no trend line over one. A nightly 1-5
//     measures the scale and the moment of typing.
//   - No resilience number. Workplace resilience programmes average d = 0.21,
//     decay to d = 0.07, and reverse for people already at risk (Vanhove et al.
//     2016) - so the word is not used as a claim anywhere in this module.
//
// The one structural decision worth stating up front: the review's questions are
// closed and forward-facing because rumination splits into brooding (predicts
// higher depression over time) and reflection (predicts lower) - Treynor,
// Gonzalez & Nolen-Hoeksema 2003. There is deliberately no free-text "how was
// your day" field anywhere in this module, and adding one would be a change to
// what the page does, not a convenience.

import { dayKey } from './contract';

// One persistent file (concept §5.2), overridable through the `reviewPath` setting.
export const DEFAULT_REVIEW_PATH = '/roam/review.org';

// Which evening the weekly thankfulness prompt appears on. 0 = Sunday, matching
// Date.getDay(). Weekly rather than daily is the concept's headline call (§2.1).
export const DEFAULT_GRATITUDE_WEEKDAY = 0;

export const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// Three good things, the shape the studied version of the exercise uses - each
// with room for why it happened, which is part of the exercise rather than
// decoration.
export const GRATITUDE_ITEMS = 3;

// "A short standing list - three to five" (§4.3). Capped rather than suggested,
// the way lib/contract.js caps its points: a values list of fifteen is not a
// values list.
export const MAX_VALUES = 5;

/**
 * The three review questions (concept §4.1).
 *
 * Fixed and closed on purpose. `happened` is descriptive, `taught` is the
 * articulation step the Di Stefano field experiment had people write, and
 * `differently` is the reflection factor of §2.2 made into a field. Nothing here
 * asks "why did I feel …", which is the brooding subscale's own phrasing.
 */
export const REVIEW_QUESTIONS = [
  {
    key: 'happened',
    label: 'What actually happened that mattered?',
    placeholder: 'One line. What happened, not how you rate it.',
  },
  {
    key: 'taught',
    label: 'What did that teach me?',
    placeholder: 'The articulation step — the part the evidence is actually about.',
  },
  {
    key: 'differently',
    label: 'What will I do differently?',
    placeholder: 'Forward-facing and concrete.',
  },
];

// Answers about an event, never about a person: did the situation arise, and if
// it did, was the plan followed (§4.4).
export const ANSWERS = ['yes', 'no'];

const DAY_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const isDayKey = (value) => typeof value === 'string' && DAY_KEY_REGEX.test(value);

const MILLIS_PER_DAY = 24 * 60 * 60 * 1000;

/** A day key back to a local midnight Date; null for anything that is not one. */
export const dateForDayKey = (key) => {
  if (!isDayKey(key)) return null;
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** Whole days from `fromKey` to `toKey`, or null if either is not a day key. */
export const daysBetween = (fromKey, toKey) => {
  const from = dateForDayKey(fromKey);
  const to = dateForDayKey(toKey);
  if (!from || !to) return null;

  // Rounded, not floored: a DST boundary inside the span shifts the difference
  // by an hour, and that must not turn 7 days into 6.
  return Math.round((to.getTime() - from.getTime()) / MILLIS_PER_DAY);
};

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');

// Every text field is stored as exactly one line of an Org file
// (lib/review_org.js), so a pasted paragraph is folded on the way in rather than
// breaking the file's structure on the way out.
const oneLine = (value) => trimmed(value).replace(/\s+/g, ' ');

const byDate = (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0);

const answerOrNull = (value) => (ANSWERS.includes(value) ? value : null);

const normalizeWeekday = (weekday) => {
  const value = Number(weekday);
  return Number.isInteger(value) && value >= 0 && value <= 6 ? value : DEFAULT_GRATITUDE_WEEKDAY;
};

/** The standing values list (§4.3): short, the person's own words, deduplicated. */
export const normalizeValues = (values) => {
  const seen = new Set();

  return (Array.isArray(values) ? values : [])
    .map((value) => oneLine(typeof value === 'string' ? value : value && value.text))
    .filter((value) => {
      if (!value || seen.has(value.toLowerCase())) return false;
      seen.add(value.toLowerCase());
      return true;
    })
    .slice(0, MAX_VALUES);
};

/**
 * Review entries, oldest first, one per day - a second review for a day that
 * already has one replaces it rather than doubling the day up. An evening is
 * revisited, not appended to.
 *
 * Only `happened` is structurally required; `taught` and `differently` may be
 * missing here even though the page's gate requires them, exactly as
 * lib/formation.js tolerates a hand-written encounter with no `felt` line. The
 * file is the user's, and an entry written in Emacs must not be discarded for
 * being incomplete.
 */
export const normalizeReviews = (reviews) => {
  const byDay = {};

  (Array.isArray(reviews) ? reviews : []).forEach((entry) => {
    if (!entry || !isDayKey(entry.date) || !oneLine(entry.happened)) return;

    byDay[entry.date] = {
      date: entry.date,
      happened: oneLine(entry.happened),
      taught: oneLine(entry.taught),
      differently: oneLine(entry.differently),
      served: oneLine(entry.served),
      rough: !!entry.rough,
      affirmed: oneLine(entry.affirmed),
    };
  });

  return Object.keys(byDay)
    .map((key) => byDay[key])
    .sort(byDate);
};

/**
 * If-then plans, oldest first, one per day (§4.4).
 *
 * `arose` and `kept` are null until answered, and null stays distinguishable
 * from 'no': a plan whose situation never came up is not a plan that was broken.
 */
export const normalizePlans = (plans) => {
  const byDay = {};

  (Array.isArray(plans) ? plans : []).forEach((entry) => {
    if (!entry || !isDayKey(entry.date)) return;

    const situation = oneLine(entry.situation);
    const action = oneLine(entry.action);
    if (!situation || !action) return;

    const arose = answerOrNull(entry.arose);

    byDay[entry.date] = {
      date: entry.date,
      situation,
      action,
      arose,
      // "Did you do the thing" only means anything once the situation arose;
      // an answer recorded against a situation that never came up is dropped
      // rather than kept as a judgement about nothing.
      kept: arose === 'yes' ? answerOrNull(entry.kept) : null,
    };
  });

  return Object.keys(byDay)
    .map((key) => byDay[key])
    .sort(byDate);
};

/** Weekly thankfulness entries, oldest first, one per day (§4.2). */
export const normalizeThankfulness = (thankfulness) => {
  const byDay = {};

  (Array.isArray(thankfulness) ? thankfulness : []).forEach((entry) => {
    if (!entry || !isDayKey(entry.date)) return;

    const items = (Array.isArray(entry.items) ? entry.items : [])
      .map((item) => ({
        thing: oneLine(item && item.thing),
        why: oneLine(item && item.why),
      }))
      .filter((item) => !!item.thing)
      .slice(0, GRATITUDE_ITEMS);

    if (!items.length) return;

    byDay[entry.date] = { date: entry.date, items };
  });

  return Object.keys(byDay)
    .map((key) => byDay[key])
    .sort(byDate);
};

/** Whatever was read out of the file into the one shape this module works on. */
export const normalizeReviewState = (state) => ({
  values: normalizeValues(state && state.values),
  reviews: normalizeReviews(state && state.reviews),
  plans: normalizePlans(state && state.plans),
  thankfulness: normalizeThankfulness(state && state.thankfulness),
});

/** A state with nothing in it yet. */
export const emptyReviewState = () => normalizeReviewState(null);

/** Append one value to the standing list (§4.3). A no-op once the list is full. */
export const addValue = (state, { text }) => {
  const normalized = normalizeReviewState(state);
  const value = oneLine(text);
  if (!value || normalized.values.length >= MAX_VALUES) return normalized;

  return { ...normalized, values: normalizeValues([...normalized.values, value]) };
};

export const removeValue = (state, text) => {
  const normalized = normalizeReviewState(state);
  const value = oneLine(text).toLowerCase();

  return {
    ...normalized,
    values: normalized.values.filter((entry) => entry.toLowerCase() !== value),
  };
};

/**
 * Write today's review (§4.1). Replaces today's if there is one already.
 *
 * Needs at least what happened; the page will not offer the button without the
 * other two, because an entry that describes without articulating or planning is
 * the half of this that the evidence does not support.
 */
export const addReview = (
  state,
  { happened, taught, differently, served, rough, affirmed, today }
) => {
  const normalized = normalizeReviewState(state);
  const line = oneLine(happened);
  if (!line) return normalized;

  return {
    ...normalized,
    reviews: normalizeReviews([
      ...normalized.reviews,
      {
        date: dayKey(today),
        happened: line,
        taught,
        differently,
        // A value the day did not serve is not recorded as one that did: only a
        // value actually on the standing list survives.
        served: normalized.values.includes(oneLine(served)) ? oneLine(served) : '',
        rough,
        affirmed,
      },
    ]),
  };
};

/** Set tomorrow's one if-then (§4.4). Both halves or nothing. */
export const addPlan = (state, { situation, action, today }) => {
  const normalized = normalizeReviewState(state);
  if (!oneLine(situation) || !oneLine(action)) return normalized;

  return {
    ...normalized,
    plans: normalizePlans([
      ...normalized.plans,
      { date: dayKey(today), situation, action, arose: null, kept: null },
    ]),
  };
};

/** Answer an earlier plan: did the situation arise, and if so was it followed. */
export const answerPlan = (state, date, { arose, kept }) => {
  const normalized = normalizeReviewState(state);

  return {
    ...normalized,
    plans: normalizePlans(
      normalized.plans.map((plan) =>
        plan.date === date
          ? { ...plan, arose: arose === undefined ? plan.arose : arose, kept }
          : plan
      )
    ),
  };
};

/** Log this week's thankfulness (§4.2). Empty items drop; all-empty is a no-op. */
export const addThankfulness = (state, { items, today }) => {
  const normalized = normalizeReviewState(state);
  const entry = { date: dayKey(today), items: Array.isArray(items) ? items : [] };
  if (!normalizeThankfulness([entry]).length) return normalized;

  return {
    ...normalized,
    thankfulness: normalizeThankfulness([...normalized.thankfulness, entry]),
  };
};

/** Today's review, or null - the page shows an existing one rather than a blank form. */
export const reviewFor = (reviews, today) => {
  const key = dayKey(today);
  return normalizeReviews(reviews).find((entry) => entry.date === key) || null;
};

/** Today's if-then, or null - setting one again today replaces it. */
export const planFor = (plans, today) => {
  const key = dayKey(today);
  return normalizePlans(plans).find((plan) => plan.date === key) || null;
};

/**
 * The most recent plan made before today that has not been answered yet (§4.4).
 *
 * Only one, and only from before today: tonight's own plan is about tomorrow and
 * asking whether it worked would be nonsense.
 */
export const openPlan = (plans, today) => {
  const key = dayKey(today);
  const entries = normalizePlans(plans).filter((plan) => plan.date < key && plan.arose === null);

  return entries.length ? entries[entries.length - 1] : null;
};

/**
 * A tally of plans, never of the person (§4.4).
 *
 * `didNotArise` is its own count rather than folded into a failure: "the
 * situation never came up" is a first-class answer, and treating it as a miss is
 * how this section would start being answered dishonestly.
 */
export const planTally = (plans) => {
  const entries = normalizePlans(plans);
  const arose = entries.filter((plan) => plan.arose === 'yes');

  return {
    total: entries.length,
    arose: arose.length,
    kept: arose.filter((plan) => plan.kept === 'yes').length,
    notKept: arose.filter((plan) => plan.kept === 'no').length,
    didNotArise: entries.filter((plan) => plan.arose === 'no').length,
    unanswered: entries.filter((plan) => plan.arose === null).length,
  };
};

/**
 * Whether tonight is a thankfulness evening (concept §4.2).
 *
 * Due on the configured weekday, and also once a full week has gone by without
 * one - a missed Sunday should not push the prompt out by another seven days.
 * Never due twice on the same day.
 */
export const gratitudeStatus = (thankfulness, today, weekday = DEFAULT_GRATITUDE_WEEKDAY) => {
  const entries = normalizeThankfulness(thankfulness);
  const latest = entries.length ? entries[entries.length - 1] : null;
  const day = normalizeWeekday(weekday);
  const todayKey = dayKey(today);

  const loggedToday = !!latest && latest.date === todayKey;
  const daysSince = latest ? daysBetween(latest.date, todayKey) : null;

  return {
    due: !loggedToday && (today.getDay() === day || daysSince === null || daysSince >= 7),
    loggedToday,
    lastDate: latest ? latest.date : null,
    daysSince,
    weekday: day,
    weekdayLabel: WEEKDAY_LABELS[day],
  };
};

/**
 * The cards of §4.5: what keeps this page honest.
 *
 * Static, and the `strength` line is content rather than decoration - a reader
 * should be able to see that the strongest thing on the page is the least
 * glamorous one.
 */
export const HONESTY_CARDS = [
  {
    text: 'The if-then plan is the strongest thing on this page. It is also the most boring.',
    why:
      'Spelling out when, where and how in advance — "if Y happens, then I will do X" — had a medium-to-large effect on goal attainment across 94 independent tests and more than 8,000 people. Nothing else here comes close.',
    strength: 'strong (Gollwitzer & Sheeran 2006, meta-analysis, d = .65)',
  },
  {
    text: 'Thankfulness is weekly here because doing it daily is how it stops working.',
    why:
      'Counting blessings once a week raised well-being over six weeks; the identical exercise three times a week did not. Repeat a positive reflection often enough and it becomes routine and stops producing the feeling that made it work.',
    strength: 'medium (Lyubomirsky, Sheldon & Schkade 2005 — one experiment, held loosely)',
  },
  {
    text:
      'Gratitude is real and small. Part of what looks like its effect is doing any exercise at all.',
    why:
      'Across 27 studies and 3,675 people, gratitude interventions had a small effect on depression and anxiety — and the effect shrank when compared against an active control rather than a waitlist.',
    strength: 'strong (Cregg & Cheavens 2021, meta-analysis, g = −0.29)',
  },
  {
    text:
      'Dwelling on why the day went badly is not the same activity as reviewing it, and it runs the other way.',
    why:
      'Rumination splits in two: brooding — passively dwelling on the reasons for distress — predicts higher depression over time, while reflection, aimed at problem-solving, predicts lower. That is why every question here is closed and points forward, and why there is no "how was your day" box.',
    strength: 'strong (Treynor, Gonzalez & Nolen-Hoeksema 2003)',
  },
  {
    text: 'This page does not build resilience, and will not tell you that it does.',
    why:
      'Workplace resilience programmes average a small effect that decays to almost nothing at follow-up — and for people already under strain and short of protective factors, the pattern reverses. A private web page cannot deliver even the small version.',
    strength: 'strong and unflattering (Vanhove et al. 2016, d = 0.21 → 0.07)',
  },
];
